import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom } from 'rxjs';
import {
  VacationPeriodData,
  VacationType,
} from '../models/holiday-care-registration.model';
import { generateVacationWeeks } from './vacation-weeks.utils';

interface OpenHolidayName {
  language: string;
  text: string;
}

interface OpenHolidayResponse {
  id: string;
  startDate: string;
  endDate: string;
  type: string;
  name: OpenHolidayName[];
  nationwide: boolean;
  subdivisions?: Array<{ code: string; shortName: string }>;
}

@Injectable({
  providedIn: 'root',
})
export class VacationCalendarService {
  private http = inject(HttpClient);

  async getVacationPeriodsForSchoolYear(
    schoolYear: number
  ): Promise<{ data: VacationPeriodData[]; fromApi: boolean }> {
    try {
      const apiData = await this.fetchFromApi(schoolYear);
      return { data: apiData, fromApi: true };
    } catch (error) {
      console.warn(
        'Failed to fetch vacation data from API, using mock data:',
        error
      );
      return { data: this.getMockVacationData(schoolYear), fromApi: false };
    }
  }

  async getVacationPeriodsForCalendarYear(
    calendarYear: number
  ): Promise<{ data: VacationPeriodData[]; fromApi: boolean }> {
    try {
      const apiData = await this.fetchFromApiCalendarYear(calendarYear);
      return { data: apiData, fromApi: true };
    } catch (error) {
      console.warn(
        'Failed to fetch vacation data from API, using mock data:',
        error
      );
      return { data: this.getMockVacationDataCalendarYear(calendarYear), fromApi: false };
    }
  }

  private getMockVacationData(schoolYear: number): VacationPeriodData[] {
    const baseYear = schoolYear;
    const nextYear = schoolYear + 1;

    // Return vacations in school year order: Autumn, Winter, Easter, Summer
    return [
      {
        name: 'Herbstferien',
        type: 'autumn' as VacationType,
        start: new Date(baseYear, 9, 6), // 6. Oktober
        end: new Date(baseYear, 9, 18), // 18. Oktober
        weeks: this.generateWeeks(
          new Date(baseYear, 9, 6),
          new Date(baseYear, 9, 18)
        ),
      },
      {
        name: 'Weihnachtsferien',
        type: 'winter' as VacationType,
        start: new Date(baseYear, 11, 22), // 22. Dezember
        end: new Date(nextYear, 0, 10), // 10. Januar
        weeks: this.generateWeeks(
          new Date(baseYear, 11, 22),
          new Date(nextYear, 0, 10)
        ),
      },
      {
        name: 'Osterferien',
        type: 'easter' as VacationType,
        start: new Date(nextYear, 2, 24), // 24. März
        end: new Date(nextYear, 3, 11), // 11. April
        weeks: this.generateWeeks(
          new Date(nextYear, 2, 24),
          new Date(nextYear, 3, 11)
        ),
      },
      {
        name: 'Sommerferien',
        type: 'summer' as VacationType,
        start: new Date(nextYear, 6, 14), // 14. Juli (following year)
        end: new Date(nextYear, 7, 29), // 29. August (following year)
        weeks: this.generateWeeks(
          new Date(nextYear, 6, 14),
          new Date(nextYear, 7, 29)
        ),
      },
    ];
  }

  /**
   * Zerlegt einen Ferienzeitraum in buchbare Wochen.
   *
   * Die Logik liegt in `vacation-weeks.utils.ts` — dort ohne Angular prüfbar,
   * inklusive der Ferien, die mitten in der Woche beginnen (#991).
   */
  private generateWeeks(
    startDate: Date,
    endDate: Date
  ): { weekNumber: number; start: Date; end: Date }[] {
    return generateVacationWeeks(startDate, endDate);
  }

  private getMockVacationDataCalendarYear(calendarYear: number): VacationPeriodData[] {
    // For calendar year, we show all vacations that fall within that year
    // Ordered by season: Spring (Easter), Summer, Autumn, Winter
    return [
      {
        name: 'Osterferien',
        type: 'easter' as VacationType,
        start: new Date(calendarYear, 2, 24), // 24. März
        end: new Date(calendarYear, 3, 11), // 11. April
        weeks: this.generateWeeks(
          new Date(calendarYear, 2, 24),
          new Date(calendarYear, 3, 11)
        ),
      },
      {
        name: 'Sommerferien',
        type: 'summer' as VacationType,
        start: new Date(calendarYear, 6, 14), // 14. Juli
        end: new Date(calendarYear, 7, 29), // 29. August
        weeks: this.generateWeeks(
          new Date(calendarYear, 6, 14),
          new Date(calendarYear, 7, 29)
        ),
      },
      {
        name: 'Herbstferien',
        type: 'autumn' as VacationType,
        start: new Date(calendarYear, 9, 6), // 6. Oktober
        end: new Date(calendarYear, 9, 18), // 18. Oktober
        weeks: this.generateWeeks(
          new Date(calendarYear, 9, 6),
          new Date(calendarYear, 9, 18)
        ),
      },
      {
        name: 'Weihnachtsferien',
        type: 'winter' as VacationType,
        start: new Date(calendarYear, 11, 22), // 22. Dezember (current year)
        end: new Date(calendarYear + 1, 0, 10), // 10. Januar (next year)
        weeks: this.generateWeeks(
          new Date(calendarYear, 11, 22),
          new Date(calendarYear + 1, 0, 10)
        ),
      },
    ];
  }

  async fetchFromApi(schoolYear: number): Promise<VacationPeriodData[]> {
    const validFrom = `${schoolYear}-07-01`;
    const validTo = `${schoolYear + 1}-06-30`;
    const apiUrl = `https://openholidaysapi.org/SchoolHolidays?countryIsoCode=DE&subdivisionCode=DE-HE&validFrom=${validFrom}&validTo=${validTo}`;

    try {
      const response$ = this.http.get<OpenHolidayResponse[]>(apiUrl).pipe(
        catchError((error) => {
          console.error('OpenHolidays API request failed:', error);
          throw new Error(
            `Failed to fetch vacation data: ${error.message || 'Unknown error'}`
          );
        })
      );

      const apiData = await firstValueFrom(response$);
      return this.mapApiResponseToVacationData(apiData, schoolYear);
    } catch (error) {
      console.warn('Failed to fetch from OpenHolidays API:', error);
      throw error;
    }
  }

  async fetchFromApiCalendarYear(calendarYear: number): Promise<VacationPeriodData[]> {
    // Fetch holidays for the calendar year, plus next January to catch winter holidays that extend
    const validFrom = `${calendarYear}-01-01`;
    const validTo = `${calendarYear + 1}-01-31`;
    const apiUrl = `https://openholidaysapi.org/SchoolHolidays?countryIsoCode=DE&subdivisionCode=DE-HE&validFrom=${validFrom}&validTo=${validTo}`;

    try {
      const response$ = this.http.get<OpenHolidayResponse[]>(apiUrl).pipe(
        catchError((error) => {
          console.error('OpenHolidays API request failed:', error);
          throw new Error(
            `Failed to fetch vacation data: ${error.message || 'Unknown error'}`
          );
        })
      );

      const apiData = await firstValueFrom(response$);
      return this.mapApiResponseToVacationDataCalendarYear(apiData, calendarYear);
    } catch (error) {
      console.warn('Failed to fetch from OpenHolidays API:', error);
      throw error;
    }
  }

  private mapApiResponseToVacationData(
    apiData: OpenHolidayResponse[],
    schoolYear: number
  ): VacationPeriodData[] {
    if (!Array.isArray(apiData)) {
      throw new Error('Invalid API response format');
    }

    const vacationMapping: {
      [key: string]: { name: string; type: VacationType };
    } = {
      Sommerferien: { name: 'Sommerferien', type: 'summer' },
      'Summer Holidays': { name: 'Sommerferien', type: 'summer' },
      Herbstferien: { name: 'Herbstferien', type: 'autumn' },
      'Autumn Holidays': { name: 'Herbstferien', type: 'autumn' },
      Weihnachtsferien: { name: 'Weihnachtsferien', type: 'winter' },
      'Christmas Holidays': { name: 'Weihnachtsferien', type: 'winter' },
      Osterferien: { name: 'Osterferien', type: 'easter' },
      'Easter Holidays': { name: 'Osterferien', type: 'easter' },
    };

    const vacationPeriods: VacationPeriodData[] = [];
    const vacationTypeMap = new Map<VacationType, VacationPeriodData>();

    for (const item of apiData) {
      // Try to get German name first, then English as fallback
      const germanName =
        item.name?.find((n) => n.language === 'DE')?.text || '';
      const englishName =
        item.name?.find((n) => n.language === 'EN')?.text || '';
      const holidayName = germanName || englishName;

      const mapping = vacationMapping[holidayName];

      if (mapping && item.startDate && item.endDate) {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);
        const startYear = startDate.getFullYear();

        // Filter to ensure we get the right vacation for the school year
        const shouldInclude = this.shouldIncludeVacationForSchoolYear(
          mapping.type,
          startYear,
          schoolYear
        );

        if (shouldInclude) {
          const vacationData: VacationPeriodData = {
            name: mapping.name,
            type: mapping.type,
            start: startDate,
            end: endDate,
            weeks: this.generateWeeks(startDate, endDate),
          };

          // Only keep one vacation per type, prioritizing the correct year
          if (!vacationTypeMap.has(mapping.type)) {
            vacationTypeMap.set(mapping.type, vacationData);
          }
        }
      }
    }

    // Convert map to array and sort in school year order
    vacationPeriods.push(...vacationTypeMap.values());

    if (vacationPeriods.length === 0) {
      console.warn(
        'No vacation periods found in API response, using mock data'
      );
      return this.getMockVacationData(schoolYear);
    }

    // Sort vacations in school year order: autumn, winter, easter, summer
    const vacationOrder: VacationType[] = ['autumn', 'winter', 'easter', 'summer'];
    vacationPeriods.sort((a, b) => {
      const indexA = vacationOrder.indexOf(a.type);
      const indexB = vacationOrder.indexOf(b.type);
      return indexA - indexB;
    });

    return vacationPeriods;
  }

  private mapApiResponseToVacationDataCalendarYear(
    apiData: OpenHolidayResponse[],
    calendarYear: number
  ): VacationPeriodData[] {
    if (!Array.isArray(apiData)) {
      throw new Error('Invalid API response format');
    }

    const vacationMapping: {
      [key: string]: { name: string; type: VacationType };
    } = {
      Sommerferien: { name: 'Sommerferien', type: 'summer' },
      'Summer Holidays': { name: 'Sommerferien', type: 'summer' },
      Herbstferien: { name: 'Herbstferien', type: 'autumn' },
      'Autumn Holidays': { name: 'Herbstferien', type: 'autumn' },
      Weihnachtsferien: { name: 'Weihnachtsferien', type: 'winter' },
      'Christmas Holidays': { name: 'Weihnachtsferien', type: 'winter' },
      Osterferien: { name: 'Osterferien', type: 'easter' },
      'Easter Holidays': { name: 'Osterferien', type: 'easter' },
    };

    const vacationPeriods: VacationPeriodData[] = [];
    const vacationTypeMap = new Map<VacationType, VacationPeriodData>();

    for (const item of apiData) {
      // Try to get German name first, then English as fallback
      const germanName =
        item.name?.find((n) => n.language === 'DE')?.text || '';
      const englishName =
        item.name?.find((n) => n.language === 'EN')?.text || '';
      const holidayName = germanName || englishName;

      const mapping = vacationMapping[holidayName];

      if (mapping && item.startDate && item.endDate) {
        const startDate = new Date(item.startDate);
        const endDate = new Date(item.endDate);
        const startYear = startDate.getFullYear();

        // For calendar year, include vacations that start in the calendar year
        const shouldInclude = startYear === calendarYear;

        if (shouldInclude) {
          const vacationData: VacationPeriodData = {
            name: mapping.name,
            type: mapping.type,
            start: startDate,
            end: endDate,
            weeks: this.generateWeeks(startDate, endDate),
          };

          // Only keep one vacation per type, prioritizing the correct year
          if (!vacationTypeMap.has(mapping.type)) {
            vacationTypeMap.set(mapping.type, vacationData);
          }
        }
      }
    }

    // Convert map to array
    vacationPeriods.push(...vacationTypeMap.values());

    if (vacationPeriods.length === 0) {
      console.warn(
        'No vacation periods found in API response, using mock data'
      );
      return this.getMockVacationDataCalendarYear(calendarYear);
    }

    // Sort vacations in calendar year order: easter, summer, autumn, winter
    const vacationOrder: VacationType[] = ['easter', 'summer', 'autumn', 'winter'];
    vacationPeriods.sort((a, b) => {
      const indexA = vacationOrder.indexOf(a.type);
      const indexB = vacationOrder.indexOf(b.type);
      return indexA - indexB;
    });

    return vacationPeriods;
  }

  private shouldIncludeVacationForSchoolYear(
    vacationType: VacationType,
    vacationStartYear: number,
    schoolYear: number
  ): boolean {
    // For a school year (e.g., 2025/2026), we want:
    // - Autumn holidays from the base year (2025)
    // - Winter holidays from the base year (2025, even though they extend into 2026)
    // - Easter holidays from the next year (2026)
    // - Summer holidays from the next year (2026) - at the end of the school year

    switch (vacationType) {
      case 'autumn':
      case 'winter':
        return vacationStartYear === schoolYear;
      case 'easter':
      case 'summer':
        return vacationStartYear === schoolYear + 1;
      default:
        return false;
    }
  }
}
